import { useEffect, useRef } from "react"
import { useSearchParams } from "react-router-dom"
import { ErrorCard } from "react-ui-components/components/ErrorCard"
import { RaffleCardSkeleton } from "react-ui-components/components/SkeletonLoader"
import { PopupService } from "react-ui-components/components/PopupService"
import { useNamespacedTranslation } from "./useNamespacedTranslation"
import { useErrorHandler } from "./useErrorHandler"
import { immersivePopupStyle } from "../utils/popupStyles"
import {
  assertSharedRaffleZip,
  loadSharedRaffle,
  SharedRaffleUnavailableError,
  withSharedRaffleDeadline,
} from "../services/raffle/sharedRaffleLink"

export const useSharedRaffleLink = ({ enabled, userZip }: { enabled: boolean; userZip: string }) => {
  const [searchParams, setSearchParams] = useSearchParams()
  const raffleId = searchParams.get("id")?.trim() || ""
  const shareCode = searchParams.get("shareCode")?.trim() || ""
  const { t } = useNamespacedTranslation(["common", "error"])
  const { handleError } = useErrorHandler("SharedRaffleLink")
  const latest = useRef({ t, handleError, userZip, setSearchParams })
  latest.current = { t, handleError, userZip, setSearchParams }

  useEffect(() => {
    if (!enabled || (!raffleId && !shareCode)) return
    let disposed = false
    let attempt = 0
    let request: AbortController | undefined
    const attribution = new AbortController()
    // Share credit is independent of direct navigation. Never wait for it when an ID
    // is present and never write a captured URL back after the callable resolves.
    const redemption = shareCode
      ? withSharedRaffleDeadline(
          import("../services/raffle/raffleShareService").then(({ redeemRaffleShareLink }) =>
            redeemRaffleShareLink(shareCode),
          ),
          attribution.signal,
        )
      : undefined
    if (raffleId) {
      void redemption?.catch((error) => {
        if (!disposed) latest.current.handleError(error, { operation: "redeem-raffle-share-link" })
      })
    }

    const onClose = () => {
      disposed = true
      request?.abort()
      attribution.abort()
      // Read the current browser URL: language/location may have changed while loading.
      const params = new URLSearchParams(window.location.search)
      if ((params.get("id")?.trim() || "") !== raffleId ||
          (params.get("shareCode")?.trim() || "") !== shareCode) return true
      params.delete("id")
      params.delete("shareCode")
      latest.current.setSearchParams(params, { replace: true })
      return true
    }

    const open = async (retry = false) => {
      const currentAttempt = ++attempt
      request?.abort()
      request = new AbortController()
      PopupService.setPopup(<RaffleCardSkeleton />, undefined, immersivePopupStyle, onClose)
      try {
        const [raffle, { default: Raffles }] = await withSharedRaffleDeadline(
          Promise.all([
            raffleId
              ? loadSharedRaffle(raffleId)
              : (retry
                  ? import("../services/raffle/raffleShareService").then(({ redeemRaffleShareLink }) =>
                      redeemRaffleShareLink(shareCode),
                    )
                  : redemption
                ).then((result) => loadSharedRaffle(result.raffleId)),
            import("../components/Raffles/Raffles"),
          ]),
          request.signal,
        )
        if (disposed || currentAttempt !== attempt) return
        assertSharedRaffleZip(raffle, latest.current.userZip)
        PopupService.setPopup(<Raffles raffle={raffle} autoHeightImages />, undefined, immersivePopupStyle, onClose)
      } catch (error) {
        if (disposed || currentAttempt !== attempt) return
        latest.current.handleError(error, { operation: "load-shared-raffle" })
        PopupService.setPopup(
          <ErrorCard
            title={latest.current.t("error:title")}
            message={latest.current.t(error instanceof SharedRaffleUnavailableError
              ? "No Raffles Available At The Moment" : "error:loadRaffles")}
            actionLabel={latest.current.t("error:tryAgain")}
            onAction={() => { if (!disposed) void open(true) }}
          />,
          undefined,
          undefined,
          onClose,
        )
      }
    }
    void open()
    return () => {
      disposed = true
      request?.abort()
      attribution.abort()
      PopupService.setPopup(null)
    }
  }, [enabled, raffleId, shareCode])
}
